import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "to", "href", "onClick", "use", "size", "isGhost"],
  _excluded2 = ["id", "endSlot", "completed", "completedMessage", "duration", "remainingTime", "footer", "description", "thumbnailSrc", "thumbnailOverlay", "thumbnailUse", "disabled", "name", "onClick", "to", "use", "size", "href", "truncateDescription", "isInternalContent"];
let _ = t => t,
  _t,
  _t2,
  _t3,
  _t4,
  _t5,
  _t6;
import { Link } from 'quartz-routing/react-router';
import styled, { css } from '../../utils/Styling';
import { useState } from 'react';
import ACSkeletonBox from '../loading/ACSkeletonBox';
import { H7 } from 'foundations-components/transitional/components/H7';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ACIcon from '../icon/ACIcon';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import I18n from 'I18n';
import ACDynamicThumbnailImage from './ACDynamicThumbnailImage';
import { Text } from 'foundations-components/components/Text';
import { NanoProgress } from 'foundations-components/transitional/components/NanoProgress';
import styledWithTheme, { useTheme } from 'foundations-theming/utils';
import { Image } from 'foundations-components/transitional/components/Image';
import { Ribbon } from 'foundations-components/transitional/components/Ribbon';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const OpaqueOverlay = styledWithTheme.div(_t || (_t = _`
  position: absolute;
  height: 100%;
  width: 100%;
  background-color: #000;
  opacity: 0.5;
`));
const linkStyles = css(["display:flex;min-height:var(--card-height);align-items:center;width:100%;border-radius:3px;gap:var(--card-gap);padding-right:8px;&:hover{background-color:var(--hover-background-color);box-shadow:var(--hover-background-color) 0px 0px 0px 4px;}color:inherit;font:inherit;line-height:inherit;text-decoration:inherit;-webkit-font-smoothing:auto;:hover{color:inherit;font:inherit;line-height:inherit;text-decoration:inherit;}"]);
const CardDimensions = {
  'extra-small': 60,
  small: 70,
  medium: 100
};
const CardGap = {
  'extra-small': 12,
  small: 12,
  medium: 16
};
const InternalLink = styledWithTheme(Link)(_t2 || (_t2 = _`
  --hover-background-color: ${0};
  --card-height: ${0}px;
  --card-gap: ${0}px;

  ${0}
`), props => props.use === 'on-light' ? props.theme.color['fill-tertiary-hover'] : props.use === 'on-surface-recessed' ? props.theme.color['fill-surface-recessed-hover'] : props.use === 'no-hover' ? 'transparent' : `${props.theme.color['fill-surface-default']}40`, ({
  size
}) => CardDimensions[size], ({
  size
}) => CardGap[size], linkStyles);
const ExternalLink = styledWithTheme.a(_t3 || (_t3 = _`
  --hover-background-color: ${0};
  --card-height: ${0}px;
  --card-gap: ${0}px;

  ${0}
`), props => props.use === 'on-light' ? props.theme.color['fill-tertiary-hover'] : props.use === 'on-surface-recessed' ? props.theme.color['fill-surface-recessed-hover'] : props.use === 'no-hover' ? 'transparent' : `${props.theme.color['fill-surface-default']}40`, ({
  size
}) => CardDimensions[size], ({
  size
}) => CardGap[size], linkStyles);
const ClickableCard = styledWithTheme.div(_t4 || (_t4 = _`
  --hover-background-color: ${0};
  --card-height: ${0}px;
  --card-gap: ${0}px;

  ${0}
`), props => props.use === 'on-light' ? props.theme.color['fill-tertiary-hover'] : props.use === 'on-surface-recessed' ? props.theme.color['fill-surface-recessed-hover'] : `${props.theme.color['fill-surface-default']}40`, ({
  size
}) => CardDimensions[size], ({
  size
}) => CardGap[size], linkStyles);
const ThumbnailOverlayWrapper = styledWithTheme.div(_t5 || (_t5 = _`
  position: absolute;
  height: 100%;
  width: 100%;
  ${0}
`), ({
  $isGhost,
  theme: trellisTheme
}) => $isGhost && css(["&::after{content:'';display:block;position:absolute;top:0;right:0;bottom:0;left:0;background-color:", ";opacity:0.6;z-index:1;pointer-events:none;}"], trellisTheme.color['icon-core-disabled']));
const StartSlot = styled.div.withConfig({
  displayName: "ACInlineContentCard__StartSlot"
})(["position:relative;height:100%;width:var(--card-height);"]);
const ThumbnailWrapper = styled.div.withConfig({
  displayName: "ACInlineContentCard__ThumbnailWrapper"
})(["position:relative;border-radius:3px;overflow:hidden;min-width:var(--card-height);aspect-ratio:1 / 1;"]);
const MiddleSlot = styledWithTheme.div(_t6 || (_t6 = _`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  flex: 1;
  ${0}
  color: ${0};
`), props => props.size === 'medium' && 'gap: 4px;', ({
  $isGhost,
  theme: trellisTheme
}) => $isGhost ? trellisTheme.color['text-core-disabled'] : 'inherit');
const EndSlot = styled.div.withConfig({
  displayName: "ACInlineContentCard__EndSlot"
})(["display:flex;align-items:center;"]);
const CardCompletedMessage = ({
  completedMessage = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "academyCommon.progress.completed"
  })
}) => {
  const trellisTheme = useTheme();
  return /*#__PURE__*/_jsxs(TFlex, {
    align: "center",
    gap: 4,
    children: [/*#__PURE__*/_jsx(ACIcon, {
      color: trellisTheme.color['fill-accent-teal-default'],
      name: "checkmark"
    }), completedMessage]
  });
};
const ContentCardLink = _ref => {
  let {
      children,
      to,
      href,
      onClick = () => {},
      use = 'on-light',
      size = 'small',
      isGhost
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const getLinkContent = () => {
    if (!isGhost) {
      if (to) {
        return (
          /*#__PURE__*/
          // @bend-expect trellis-tokenized
          _jsx(InternalLink, Object.assign({
            onClick: onClick,
            size: size,
            to: to,
            use: use
          }, rest, {
            children: children
          }))
        );
      }
      if (href) {
        return (
          /*#__PURE__*/
          // @bend-expect trellis-tokenized
          _jsx(ExternalLink, Object.assign({
            href: href,
            onClick: onClick,
            size: size,
            use: use
          }, rest, {
            children: children
          }))
        );
      }
    }
    return (
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx(ClickableCard, Object.assign({
        onClick: onClick,
        role: "button",
        size: size,
        use: use
      }, rest, {
        children: children
      }))
    );
  };
  return /*#__PURE__*/_jsx(View, {
    position: "relative",
    children: getLinkContent()
  });
};
const ACInlineContentCard = _ref2 => {
  let {
      id,
      endSlot,
      completed,
      completedMessage,
      duration,
      remainingTime,
      footer,
      description,
      thumbnailSrc,
      thumbnailOverlay,
      thumbnailUse,
      disabled = false,
      name,
      onClick = () => {},
      to,
      use = 'on-light',
      size = 'small',
      href,
      truncateDescription = true,
      isInternalContent = false
    } = _ref2,
    props = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const trellisTheme = useTheme();
  const progress = completed ? 1 : duration && remainingTime ? (duration - remainingTime) / duration : 0;
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);
  const thumbnailLoading = thumbnailSrc && !thumbnailLoaded;
  const ribbon = isInternalContent ? /*#__PURE__*/_jsx(Ribbon, {
    position: "right",
    use: "norman",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyCommon.contentCard.badge.internal"
    })
  }) : null;
  return /*#__PURE__*/_jsxs(ContentCardLink, Object.assign({
    href: href,
    isGhost: disabled,
    onClick: onClick,
    size: size,
    to: to,
    use: use
  }, props, {
    children: [/*#__PURE__*/_jsx(StartSlot, {
      children: /*#__PURE__*/_jsxs(ThumbnailWrapper, {
        children: [/*#__PURE__*/_jsx(ACSkeletonBox, {
          style: {
            position: 'absolute',
            height: '100%',
            width: '100%'
          }
        }), thumbnailSrc ? /*#__PURE__*/_jsx(Image, {
          alt: I18n.text('academyCommon.contentCard.thumbnailAlt', {
            title: name
          }),
          blockSize: "100%",
          loading: "lazy",
          onLoad: () => setThumbnailLoaded(true),
          position: "absolute",
          src: thumbnailSrc,
          visibility: thumbnailLoading ? 'hidden' : 'visible'
        }) : /*#__PURE__*/_jsx(ACDynamicThumbnailImage, {
          seed: id,
          style: {
            position: 'absolute',
            height: '100%'
          },
          title: name,
          type: "square"
        }), thumbnailUse === 'dark' && /*#__PURE__*/_jsx(OpaqueOverlay, {}), /*#__PURE__*/_jsxs(ThumbnailOverlayWrapper, {
          $isGhost: disabled,
          children: [thumbnailOverlay, /*#__PURE__*/_jsx("div", {
            style: {
              position: 'absolute',
              bottom: 0,
              width: '100%',
              height: 3
            },
            children: /*#__PURE__*/_jsx(NanoProgress, {
              "aria-label": I18n.text('academyCommon.contentCard.progressBar'),
              color: progress === 1 ? trellisTheme.color['fill-accent-teal-default'] : trellisTheme.color['fill-accent-light-orange-default'],
              height: 3,
              value: progress * 100
            })
          })]
        })]
      })
    }), /*#__PURE__*/_jsxs(MiddleSlot, {
      $isGhost: disabled,
      size: size,
      children: [size === 'small' || size === 'extra-small' ? /*#__PURE__*/_jsx(H7, {
        marginBlockEnd: "100",
        children: name
      }) : /*#__PURE__*/_jsx(Text, {
        as: "h2",
        marginBlockEnd: "0",
        typography: "heading-200",
        children: name
      }), description && (truncateDescription ? /*#__PURE__*/_jsx(TruncateString, {
        children: description
      }) : description), completed && /*#__PURE__*/_jsx(CardCompletedMessage, {
        completedMessage: completedMessage
      }), footer]
    }), /*#__PURE__*/_jsx(EndSlot, {
      children: endSlot
    }), ribbon && /*#__PURE__*/_jsx(View, {
      minInlineSize: "100px",
      children: ribbon
    })]
  }));
};
export default ACInlineContentCard;